// RapportClient.js
import React, { useState, useEffect, useMemo } from 'react';
import './RapportClient.css';
import API_CONFIG from '../components/API_CONFIG/API_CONFIG';

/**
 * RapportClient
 * - Charge les données depuis l'API (RapportClient.php)
 * - Affiche statistiques + charts
 * - Remplace la liste de clients par un DataTable (liste des locations) :
 *   chaque ligne affiche : image du véhicule, marque, modèle, client, email, téléphone, prix total, durée.
 *
 * NOTE : j'utilise les champs attendus suivants dans les objets reçus depuis l'API:
 *  - image1 (nom/fichier de l'image du véhicule)
 *  - marque, modele
 *  - nom_complet, email, telephone
 *  - prix_total, duree
 *
 * Si vos champs ont d'autres noms, adaptez getImageUrl(...) et les accès aux propriétés.
 */

const RapportClient = () => {
  const [clients, setClients] = useState([]); // chaque entrée = une location / rapport
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalClients: 0,
    totalLocations: 0,
    chiffreAffaireTotal: 0,
    dureeMoyenne: 0,
    meilleurClient: null
  });

  // DataTable states
  const [dtSearch, setDtSearch] = useState('');
  const [sortBy, setSortBy] = useState({ column: 'prix_total', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetch(`${API_CONFIG.apiUrl}RapportClient.php`);
      const data = await response.json();
      if (data.success) {
        // assume data.datas is an array of locations
        setClients(Array.isArray(data.datas) ? data.datas : []);
        calculateStats(Array.isArray(data.datas) ? data.datas : []);
      } else {
        setClients([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (clientsData) => {
    const totalClients = new Set(clientsData.map(c => `${c.telephone}-${c.email}`)).size;
    const totalLocations = clientsData.length;
    const chiffreAffaireTotal = clientsData.reduce((sum, client) => {
      const p = parseFloat(client.prix_total || 0);
      return sum + (isNaN(p) ? 0 : p);
    }, 0);
    const dureeMoyenne = totalLocations === 0
      ? 0
      : clientsData.reduce((sum, client) => sum + (parseInt(client.duree || 0) || 0), 0) / totalLocations;

    // meilleur client (plus de locations)
    const clientLocations = {};
    clientsData.forEach(client => {
      const key = `${client.telephone}-${client.email}`;
      clientLocations[key] = (clientLocations[key] || 0) + 1;
    });
    let meilleurClientKey = null;
    let max = -1;
    Object.keys(clientLocations).forEach(k => {
      if (clientLocations[k] > max) {
        max = clientLocations[k];
        meilleurClientKey = k;
      }
    });
    const meilleurClient = meilleurClientKey
      ? clientsData.find(client => `${client.telephone}-${client.email}` === meilleurClientKey)
      : null;

    setStats({
      totalClients,
      totalLocations,
      chiffreAffaireTotal,
      dureeMoyenne,
      meilleurClient: meilleurClient ? { ...meilleurClient, nbLocations: clientLocations[meilleurClientKey] } : null
    });
  };

  /* ---------- DataTable logic ---------- */

  const normalizedSearch = (s = '') => s.trim().toLowerCase();

  const filteredData = useMemo(() => {
    const s = normalizedSearch(dtSearch);
    let arr = clients.slice();

    if (s) {
      arr = arr.filter(item => {
        const hay = [
          item.nom_complet,
          item.email,
          item.telephone,
          item.marque,
          item.modele,
          item.immatriculation,
          String(item.prix_total),
          String(item.duree)
        ].join(' ').toLowerCase();
        return hay.includes(s);
      });
    }

    // Sorting
    const { column, direction } = sortBy;
    arr.sort((a, b) => {
      const A = (a[column] ?? '').toString().toLowerCase();
      const B = (b[column] ?? '').toString().toLowerCase();

      // If numeric column (prix_total, duree) compare as numbers
      if (column === 'prix_total' || column === 'duree') {
        const nA = parseFloat(a[column] ?? 0) || 0;
        const nB = parseFloat(b[column] ?? 0) || 0;
        return direction === 'asc' ? nA - nB : nB - nA;
      }

      if (A < B) return direction === 'asc' ? -1 : 1;
      if (A > B) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    return arr;
  }, [clients, dtSearch, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPages]);

  const currentData = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, currentPage, rowsPerPage]);

  const toggleSort = (column) => {
    setSortBy(prev => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: 'asc' };
    });
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/placeholder-vehicle.jpg';
    return `${API_CONFIG.apiUrl}carpic/${imagePath}`;
  };

  /* ---------- Render ---------- */

  if (loading) {
    return (
      <div className="rapport-client-container">
        <div className="loading">Chargement des données...</div>
      </div>
    );
  }

  return (
    <div className="rapport-client-container">
      <header className="dashboard-header">
        <h1>Rapport Clients</h1>
        <p>Analyse des performances et statistiques clients</p>
      </header>

      <div className="stats-grid">
        <StatCard title="Total Clients" value={stats.totalClients} icon="👥" color="#8A2BE2" />
        <StatCard title="Total Locations" value={stats.totalLocations} icon="🚗" color="#9370DB" />
        <StatCard title="Chiffre d'Affaire" value={`${stats.chiffreAffaireTotal.toFixed(2)} $`} icon="💰" color="#BA55D3" />
        <StatCard title="Durée Moyenne" value={`${(stats.dureeMoyenne || 0).toFixed(1)} jours`} icon="📅" color="#DA70D6" />
      </div>

      <div className="content-grid">
        <div className="chart-section glass-card">
          <h3>Répartition des Locations</h3>
          <DonutChart clients={clients} />
        </div>

        <div className="meilleur-client-section glass-card">
          <h3>Meilleur Client</h3>
          {stats.meilleurClient ? (
            <div className="meilleur-client">
              <div className="client-avatar">
                {stats.meilleurClient.nom_complet ? stats.meilleurClient.nom_complet.split(' ').map(n => n[0]).join('') : 'NC'}
              </div>
              <div className="client-info">
                <h4>{stats.meilleurClient.nom_complet}</h4>
                <p>{stats.meilleurClient.email}</p>
                <p>{stats.meilleurClient.telephone}</p>
                <div className="client-stats">
                  <span>{stats.meilleurClient.nbLocations} locations</span>
                </div>
              </div>
            </div>
          ) : <div>Aucun client</div>}
        </div>

        <div className="progress-section glass-card">
          <h3>Performance Clients</h3>
          <ClientProgress clients={clients} />
        </div>

        {/* DataTable Section */}
        <div className="clients-list-section glass-card" style={{ gridColumn: '1 / -1' }}>
          <h3>Liste des Locations (DataTable)</h3>

          <div className="datatable-controls" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <input
              type="search"
              placeholder="Rechercher nom, marque, modèle, immatriculation..."
              value={dtSearch}
              onChange={(e) => { setDtSearch(e.target.value); setCurrentPage(1); }}
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', minWidth: 260 }}
            />

            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Lignes par page:
              <select value={rowsPerPage} onChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setCurrentPage(1); }}>
                {[5, 10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>

            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button onClick={() => { setDtSearch(''); setSortBy({ column: 'prix_total', direction: 'desc' }); setCurrentPage(1); }}>
                Réinitialiser
              </button>
            </div>
          </div>

          <div className="datatable-wrapper" style={{ overflowX: 'auto' }}>
            <table className="datatable" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>#</th>
                  <th style={thStyle}>Image</th>
                  <th style={thClickable} onClick={() => toggleSort('marque')}>
                    Marque / Modèle {sortBy.column === 'marque' && renderSortIcon(sortBy.direction)}
                  </th>
                  <th style={thClickable} onClick={() => toggleSort('nom_complet')}>
                    Client {sortBy.column === 'nom_complet' && renderSortIcon(sortBy.direction)}
                  </th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Téléphone</th>
                  <th style={thClickable} onClick={() => toggleSort('prix_total')}>
                    Prix total {sortBy.column === 'prix_total' && renderSortIcon(sortBy.direction)}
                  </th>
                  <th style={thClickable} onClick={() => toggleSort('duree')}>
                    Durée (jours) {sortBy.column === 'duree' && renderSortIcon(sortBy.direction)}
                  </th>
                </tr>
              </thead>

              <tbody>
                {currentData.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ padding: 20, textAlign: 'center' }}>Aucune ligne trouvée.</td>
                  </tr>
                ) : currentData.map((row, idx) => (
                  <tr key={`${row.telephone}-${row.email}-${idx}`} style={rowStyle}>
                    <td style={tdStyle}>{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                    <td style={tdStyle}>
                      <img
                        src={getImageUrl(row.image1)}
                        alt={`${row.marque || ''} ${row.modele || ''}`}
                        style={{ width: 80, height: 50, objectFit: 'cover', borderRadius: 6, border: '1px solid #eee' }}
                        onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder-vehicle.jpg'; }}
                      />
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 700 }}>{row.marque || 'N/C'}</div>
                      <div style={{ fontSize: 13, color: '#666' }}>{row.modele || ''}</div>
                    </td>
                    <td style={tdStyle}>{row.nom_complet || 'N/C'}</td>
                    <td style={tdStyle}>{row.email || 'N/C'}</td>
                    <td style={tdStyle}>{row.telephone || 'N/C'}</td>
                    <td style={tdStyle}>{(parseFloat(row.prix_total || 0) || 0).toFixed(2)} $</td>
                    <td style={tdStyle}>{row.duree ?? 'N/C'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="datatable-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <div>
              Affichage {filteredData.length === 0 ? 0 : ( (currentPage-1)*rowsPerPage + 1 )} - {Math.min(currentPage*rowsPerPage, filteredData.length)} sur {filteredData.length}
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>«</button>
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>‹</button>
              <span>Page</span>
              <input
                type="number"
                value={currentPage}
                onChange={(e) => {
                  let p = parseInt(e.target.value || '1', 10);
                  if (isNaN(p) || p < 1) p = 1;
                  if (p > totalPages) p = totalPages;
                  setCurrentPage(p);
                }}
                style={{ width: 56, textAlign: 'center' }}
              />
              <span> / {totalPages}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>›</button>
              <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>»</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------- small helpers & components ---------- */

const thStyle = {
  textAlign: 'left',
  padding: '10px 12px',
  borderBottom: '1px solid #e6e6e6',
  fontWeight: 700,
  fontSize: 14,
};

const thClickable = {
  ...thStyle,
  cursor: 'pointer',
  userSelect: 'none'
};

const tdStyle = {
  padding: '10px 12px',
  borderBottom: '1px solid #f0f0f0',
  verticalAlign: 'middle'
};

const rowStyle = {
  background: '#fff',
  color: '#222'
};

function renderSortIcon(direction) {
  return direction === 'asc' ? ' ▲' : ' ▼';
}

const StatCard = ({ title, value, icon, color }) => (
  <div className="stat-card glass-card" style={{ borderLeft: `4px solid ${color}` }}>
    <div className="stat-icon" style={{ backgroundColor: color }}>
      {icon}
    </div>
    <div className="stat-content">
      <h3>{title}</h3>
      <p>{value}</p>
    </div>
  </div>
);

const DonutChart = ({ clients }) => {
  const locationsParClient = clients.reduce((acc, client) => {
    const key = client.nom_complet || 'Anonyme';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const topClients = Object.entries(locationsParClient)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const total = topClients.reduce((sum, [_, count]) => sum + count, 0) || 1;

  return (
    <div className="donut-chart">
      {topClients.map(([client, count], index) => {
        const percentage = ((count / total) * 100).toFixed(1);
        const colors = ['#8A2BE2', '#9370DB', '#BA55D3', '#DA70D6', '#EE82EE'];

        return (
          <div key={client} className="chart-item">
            <div className="chart-label">
              <span className="client-name">{client}</span>
              <span className="client-percentage">{percentage}%</span>
            </div>
            <div className="chart-bar">
              <div
                className="chart-fill"
                style={{
                  width: `${percentage}%`,
                  backgroundColor: colors[index % colors.length]
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ClientProgress = ({ clients }) => {
  const locationsParClient = clients.reduce((acc, client) => {
    const key = client.nom_complet || 'Anonyme';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const maxLocations = Math.max(0, ...Object.values(locationsParClient));

  return (
    <div className="client-progress">
      {Object.entries(locationsParClient)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6)
        .map(([client, count]) => {
          const percentage = maxLocations === 0 ? 0 : (count / maxLocations) * 100;
          return (
            <div key={client} className="progress-item">
              <div className="progress-info">
                <span className="client-name">{client}</span>
                <span className="location-count">{count} location(s)</span>
              </div>
              <div className="progress-bar-container">
                <div className="progress-bar" style={{ width: `${percentage}%` }}></div>
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default RapportClient;
